import { DotText } from "@/v2-backoffice-client/components/dot"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import LayerDriftSeverityBadge from "@/v2-backoffice-client/layer/drift/LayerDriftSeverityBadge"
import { useLayerDriftSummary } from "@/v2-backoffice-client/layer/drift/LayerDriftMapContext"
import type { SeverityLabel } from "@/v2-backoffice-client/layer/drift/layer.drift.utils"
import type { LayerDriftSummaryFragment } from "@/v2-backoffice-client/uptime/dashboard/__types__/dashboard.driftSummaries.fragment"

export type LayerDriftCellProps = {
  featureCode: string | null | undefined
}

/**
 * One-row binding for the layer tables' "Drift" column. Reads from the
 * shared lookup populated by `LayerDriftMapProvider` and condenses the
 * summary to a single verdict: in sync, drifting (highest-severity pill
 * with the rollup in the tooltip), or not fingerprinted yet. The full
 * diff lives in the layer sheet's Drift tab.
 */
function LayerDriftCell(props: Readonly<LayerDriftCellProps>) {
  const { summary, loading } = useLayerDriftSummary(props.featureCode)

  if (loading && !summary) {
    return <span className="text-muted-foreground/70 text-xs">Loading…</span>
  }

  if (!summary) {
    return (
      <span
        className="flex items-center gap-1.5 text-xs text-muted-foreground"
        title="Not fingerprinted yet — the drift monitor baselines a layer after its first successful metadata probe."
      >
        <Dot variant="muted" />
        <span>—</span>
      </span>
    )
  }

  const hasCountDrift = Boolean(summary.CountDrift?.HasCountDrift)
  if (!summary.HasDrift && !hasCountDrift) {
    return <DotText variant="success-1">In sync</DotText>
  }

  return (
    <LayerDriftSeverityBadge
      severity={getCellSeverity(summary)}
      title={buildTooltip(summary)}
    >
      {buildLabel(summary)}
    </LayerDriftSeverityBadge>
  )
}

function getCellSeverity(summary: LayerDriftSummaryFragment): SeverityLabel {
  if (summary.HasDrift) {
    return (summary.HighestSeverity as SeverityLabel) || "info"
  }
  // Count-drift-only rows: the upstream metadata is identical but the
  // filtered feature count diverged from the accepted baseline.
  return summary.CountDrift?.Severity === "breaking" ? "breaking" : "warning"
}

function buildLabel(summary: LayerDriftSummaryFragment): string {
  if (summary.BreakingCount > 0) return `${summary.BreakingCount} breaking`
  if (summary.WarningCount > 0) return `${summary.WarningCount} warning`
  if (summary.InfoCount > 0) return `${summary.InfoCount} info`
  return "count drift"
}

function buildTooltip(summary: LayerDriftSummaryFragment): string {
  const parts: string[] = []
  if (summary.BreakingCount > 0) parts.push(`${summary.BreakingCount} breaking`)
  if (summary.WarningCount > 0) parts.push(`${summary.WarningCount} warning`)
  if (summary.InfoCount > 0) parts.push(`${summary.InfoCount} info`)
  const countDrift = summary.CountDrift
  if (countDrift?.HasCountDrift) {
    parts.push(
      `feature count drifted (${countDrift.BaselineCount ?? "?"} → ${countDrift.CurrentCount ?? "?"})`,
    )
  }
  return `${parts.join(" · ")} — open the layer's Drift tab for the diff`
}

export default LayerDriftCell
